import { useEffect, useState } from "react";
import { Bell, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { isPushSupported, enablePushNotifications, getPushSubscriptionStatus } from "@/lib/pwa";

/**
 * Bildirim aktivasyon banner'ı.
 *
 * KURYE TELEFONUNDA GÖRÜNÜR: PWA standalone modda açıldığında, push desteği
 * varsa, henüz subscribe olmamış ve daha önce "kapat" dememişse görünür.
 *
 * iOS Safari KRİTİK GEREKLİLİĞİ:
 * - Notification.requestPermission() ÇAĞRISI BİR KULLANICI DOKUNUŞU İÇİNDE
 *   YAPILMAK ZORUNDADIR. setTimeout / useEffect içinde çağrılırsa iOS sessizce
 *   reddeder ve hiçbir izin penceresi GÖSTERMEZ. Bu yüzden banner kullanıyoruz:
 *   kullanıcı "Aç" butonuna basınca request yapılır.
 *
 * Android Chrome / desktop'ta da çalışır (gereksiz değil — kullanıcı kasten
 * aç dediği için daha yüksek izin oranı).
 */
export function PushActivationBanner() {
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      if (!isPushSupported()) return;
      // Önceden kullanıcı "şimdi değil" demiş mi?
      if (localStorage.getItem("tt_push_banner_dismissed") === "1") return;
      const status = await getPushSubscriptionStatus().catch(() => null);
      if (!status) return;
      // Zaten abone — banner gerekmez
      if (status.subscribed) return;
      // Tarayıcıda permission "denied" ise banner göstermek anlamsız
      if (status.permission === "denied") return;

      // iOS standalone kontrolü — Safari sekmesinde gizle (push çalışmaz)
      const isiOS = /iPad|iPhone|iPod/.test(navigator.userAgent || "");
      const isStandalone =
        window.matchMedia?.("(display-mode: standalone)").matches ||
        window.navigator?.standalone === true;
      if (isiOS && !isStandalone) return;

      if (mounted) setShow(true);
    })();
    return () => { mounted = false; };
  }, []);

  if (!show) return null;

  const enable = async () => {
    setBusy(true);
    try {
      const res = await enablePushNotifications();
      if (res.ok) {
        toast.success("Bildirimler aktif — yeni siparişlerde size haber verilecek");
        setShow(false);
      } else {
        toast.error(res.reason || "Bildirim açılamadı");
        // izin reddedildiyse banner'ı kapat — kullanıcı tarayıcı ayarından açabilir
        if (Notification.permission === "denied") setShow(false);
      }
    } catch (e) {
      toast.error(e?.message || "Beklenmeyen hata");
    } finally {
      setBusy(false);
    }
  };

  const dismiss = () => {
    localStorage.setItem("tt_push_banner_dismissed", "1");
    setShow(false);
  };

  return (
    <div
      data-testid="push-activation-banner"
      className="bg-amber-50 border-2 border-amber-400 p-3 sm:p-4 mb-4 flex items-start gap-3"
    >
      <div className="w-10 h-10 bg-amber-500 text-zinc-950 flex items-center justify-center shrink-0">
        <Bell size={18} strokeWidth={2.5} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-black text-sm text-zinc-950 mb-0.5">Bildirimleri Aç</div>
        <div className="text-xs text-zinc-700 leading-relaxed">
          Yeni sipariş geldiğinde telefonunuza anında bildirim gelsin. Kilitliyken bile sesli uyarı + titreşim.
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          <button
            type="button"
            data-testid="push-banner-enable"
            onClick={enable}
            disabled={busy}
            className="inline-flex items-center gap-1.5 px-3 py-2 bg-zinc-950 text-white text-xs font-black uppercase tracking-wider border-b-4 border-black active:border-b-0 active:translate-y-[3px] transition-all disabled:opacity-60"
          >
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Bell size={14} strokeWidth={2.5} />}
            Bildirimleri Aç
          </button>
          <button
            type="button"
            data-testid="push-banner-dismiss"
            onClick={dismiss}
            disabled={busy}
            className="inline-flex items-center gap-1.5 px-3 py-2 border-2 border-zinc-300 text-zinc-600 text-xs font-bold uppercase tracking-wider hover:border-zinc-950 hover:text-zinc-950"
          >
            Şimdi Değil
          </button>
        </div>
      </div>
      <button
        type="button"
        onClick={dismiss}
        className="p-1 text-zinc-400 hover:text-zinc-950 shrink-0"
        aria-label="Kapat"
      >
        <X size={16} />
      </button>
    </div>
  );
}
